import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

interface HeaderProps {
  searchQuery?: string;
  onSearchChange?: (value: string) => void;
}

export default function Header({ searchQuery = '', onSearchChange }: HeaderProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'mn' : 'en';
    i18n.changeLanguage(newLang);
  };

  const isMapPage = location.pathname === '/map';

  const handleSearchClick = () => {
    setIsSearchOpen(!isSearchOpen);
  };

  const handleSearchChange = (value: string) => {
    if (onSearchChange) {
      onSearchChange(value);
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
      <div className="px-3 md:px-6 py-3 md:py-4">
        {/* Desktop & Mobile Collapsed Header */}
        <div className={`flex items-center justify-between gap-2 ${isSearchOpen ? 'md:flex hidden' : 'flex'}`}>
          {/* Logo + Site Name */}
          <div className="flex items-center gap-2 min-w-0">
            <img
              src="https://public.readdy.ai/ai/img_res/83d324a8-1283-48fe-b12d-424b8ff3757f.png"
              alt="Discover UB Logo"
              className="h-8 md:h-10 w-auto object-contain flex-shrink-0"
            />
            <h1 className="text-base md:text-2xl font-bold text-gray-900 truncate">{t('siteName')}</h1>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Search Icon - Mobile Only */}
            <button
              onClick={handleSearchClick}
              className="md:hidden flex items-center justify-center w-9 h-9 text-gray-700 hover:text-gray-900 bg-gray-50 hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
              aria-label={t('searchPlaceholder')}
            >
              <i className="ri-search-line text-lg"></i>
            </button>

            {/* Home / Map toggle button */}
            <button
              onClick={() => navigate(isMapPage ? '/' : '/map')}
              className="flex items-center gap-1.5 px-3 py-2 text-xs md:text-sm font-medium text-gray-700 hover:text-gray-900 bg-gray-50 hover:bg-gray-100 rounded-full transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className={`ri-${isMapPage ? 'home' : 'map'}-line text-base`}></i>
              <span className="hidden sm:inline">{isMapPage ? t('nav.home') : t('nav.mapView')}</span>
            </button>

            {/* Language toggle */}
            <button
              onClick={toggleLanguage}
              className="px-3 py-2 text-xs md:text-sm font-medium text-gray-700 hover:text-gray-900 bg-gray-50 hover:bg-gray-100 rounded-full transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('switchLanguage')}
            </button>
          </div>
        </div>

        {/* Mobile Expanded Search Bar */}
        {isSearchOpen && (
          <div className="md:hidden flex items-center gap-2 mt-3">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <i className="ri-search-line text-base text-gray-400"></i>
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder={t('searchPlaceholder')}
                className="w-full pl-10 pr-3 py-2.5 text-sm border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent shadow-sm cursor-text"
                autoFocus
              />
            </div>
            <button
              onClick={handleSearchClick}
              className="flex items-center justify-center w-9 h-9 text-gray-700 hover:text-gray-900 bg-gray-50 hover:bg-gray-100 rounded-full transition-colors cursor-pointer flex-shrink-0"
              aria-label="Close search"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
        )}
      </div>
    </header>
  );
}